import { Column, Row, Table } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { cn, groupBy, pluralize, useDebounceState } from "@theprelude/core";
import {
  DropdownTrigger,
  IconButton,
  MoreIcon,
  DropdownContent,
  DropdownLabel,
  DropdownCheckbox,
  Dropdown,
  Input,
  SearchIcon,
  CloseIcon,
  DropdownSeparator,
  Truncate,
  Button,
  DownloadIcon,
} from "@theprelude/ds";
import { mkConfig, generateCsv, download } from "export-to-csv";
import { useMemo, useState, useRef } from "react";
import { fuzzyFilter } from "@/lib/fuzzy-filters";
import { Highlight } from "../highlight";
import { resetTableVisibility } from "./hooks";

const csvConfig = mkConfig({
  fieldSeparator: ",",
  filename: "export",
  useKeysAsHeaders: true,
});

const isObject = (value: any) => value !== null && typeof value === "object";

const exportCSV = (rows: Row<any>[], columns: Column<any, unknown>[]) => {
  const columnsNames: Record<
    string,
    {
      label: string;
      exportFn?: (data: any) => string;
    }
  > = {};

  for (const column of columns) {
    if (
      column.columnDef.meta?.label &&
      column.columnDef.meta?.canExport !== false
    ) {
      columnsNames[column.id] = {
        label: column.columnDef.meta?.label,
        exportFn: column.columnDef.meta?.exportFn,
      };
    }
  }

  const rowData = rows.map((row) => {
    const data: Record<string, any> = {};
    for (const columnId of Object.keys(columnsNames)) {
      const label = columnsNames[columnId]?.label;
      const exportFn = columnsNames[columnId]?.exportFn;

      if (label) {
        const raw = row.getValue(columnId);
        const value = exportFn ? exportFn(raw) : raw;

        data[label] = isObject(value) ? JSON.stringify(value) : value;
      }
    }

    return data;
  });

  const csv = generateCsv(csvConfig)(rowData);
  download(csvConfig)(csv);
};

function sortColumns(a: Column<any, unknown>, b: Column<any, unknown>) {
  const labelA = a.columnDef.meta?.label ?? "";
  const labelB = b.columnDef.meta?.label ?? "";
  return labelA.localeCompare(labelB);
}

export function TableOptions({ table }: { table: Table<any> }) {
  const [view, setView] = useState<"advanced" | "quick">("advanced");
  const hideableColumns = table.getAllColumns().filter((c) => c.getCanHide());
  const columns = groupBy(
    hideableColumns,
    (column) => column.columnDef.meta?.filter?.group ?? "",
  );

  const canExport = table
    .getAllFlatColumns()
    .some((column) => column.columnDef.meta?.canExport);

  const canHideColumns = hideableColumns.length > 0;

  const sortedColumns = Object.keys(columns)
    .sort()
    .reduce(
      (acc, key) => {
        acc[key] = columns[key]?.sort(sortColumns) ?? [];
        return acc;
      },
      {} as Record<string, Column<any, unknown>[]>,
    );

  const columnTypes = Object.keys(columns).flat();
  const canQuickFilter = columnTypes.length > 1;

  const showFooter = canExport || canQuickFilter;

  return (
    <div className="absolute right-0 z-20 flex h-[40px] items-center bg-secondary-background p-2">
      <Dropdown>
        <DropdownTrigger asChild>
          <IconButton icon={<MoreIcon />} />
        </DropdownTrigger>
        <DropdownContent align="end" className="w-full">
          {canHideColumns && view === "advanced" ? (
            <AdvancedView columns={sortedColumns} table={table} />
          ) : (
            <QuickView columns={sortedColumns} table={table} />
          )}
          <DropdownSeparator />
          {showFooter && (
            <div className="flex items-center justify-between px-3 py-2">
              <ExportButton table={table} canExport={canExport} />
              {canQuickFilter && (
                <Button
                  onClick={() =>
                    setView((prevView) =>
                      prevView === "advanced" ? "quick" : "advanced",
                    )
                  }
                  className="p-0 text-xs"
                >
                  Switch to {view === "advanced" ? "quick" : "advanced"} filters
                </Button>
              )}
            </div>
          )}
        </DropdownContent>
      </Dropdown>
    </div>
  );
}

function QuickView<TData>({
  columns,
  table,
}: {
  columns: Record<string, Column<any, unknown>[]>;
  table: Table<TData>;
}) {
  return (
    <div className="max-h-[450px] w-[800px]">
      <div className="flex items-center justify-between px-3 py-2">
        <span className="text-base font-medium">Quick Filters</span>
        {table.options.meta?.name && (
          <RestoreDefault tableName={table.options.meta?.name} />
        )}
      </div>
      <DropdownSeparator />
      <div className="overflow-auto">
        <div className="flex items-start justify-start gap-3 p-3">
          {Object.entries(columns).map(([label, columns]) => (
            <div key={label}>
              <DropdownLabel className="px-2">
                {label || "Columns"}
              </DropdownLabel>
              {columns.sort(sortColumns).map((column) => {
                const label = column.columnDef.meta?.label || column.id;
                return (
                  <DropdownCheckbox
                    className={"px-3"}
                    key={column.id}
                    checked={column.getIsVisible()}
                    onSelect={(event) => event.preventDefault()}
                    onCheckedChange={(value) =>
                      column.toggleVisibility(!!value)
                    }
                  >
                    <Truncate text={label}>{label}</Truncate>
                  </DropdownCheckbox>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AdvancedView({
  columns,
  table,
}: {
  columns: Record<string, Column<any, unknown>[]>;
  table: Table<any>;
}) {
  const searchTerm = useDebounceState<string>("", 500);
  const parentRef = useRef<HTMLDivElement>(null);

  const filteredColumns = useMemo(() => {
    return Object.entries(columns).reduce(
      (acc, [groupLabel, groupColumns]) => {
        const filtered = groupColumns.filter((column) => {
          const label = column.columnDef.meta?.label || column.id;
          return fuzzyFilter(
            label.toLowerCase(),
            searchTerm.debouncedValue.toLocaleLowerCase(),
          );
        });

        if (filtered.length > 0) {
          acc[groupLabel] = filtered;
        }
        return acc;
      },
      {} as Record<string, Column<any, unknown>[]>,
    );
  }, [columns, searchTerm.debouncedValue]);

  type ListItem =
    | { type: "label"; label: string }
    | { type: "column"; column: Column<any, unknown>; groupLabel?: string };

  const flattenedItems = useMemo<ListItem[]>(() => {
    const items: ListItem[] = [];
    Object.entries(filteredColumns).forEach(([label, columns]) => {
      items.push({ type: "label", label: label || "Columns" });
      columns.sort(sortColumns).forEach((column) => {
        items.push({ type: "column", column, groupLabel: label });
      });
    });
    return items;
  }, [filteredColumns]);

  const allFilteredColumns = Object.values(filteredColumns).flat();
  const allFilteredHideableColumns = allFilteredColumns.filter((column) =>
    column.getCanHide(),
  );
  const isAllFilteredVisible = allFilteredHideableColumns.every((column) =>
    column.getIsVisible(),
  );

  const toggleAllFiltered = (value: boolean) => {
    const nextVisibilityState = Object.fromEntries(
      allFilteredHideableColumns.map((column) => [column.id, value]),
    );
    table.setColumnVisibility((prevState) => ({
      ...prevState,
      ...nextVisibilityState,
    }));
  };

  const rowVirtualizer = useVirtualizer({
    count: flattenedItems.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) =>
      flattenedItems[index]?.type === "label" ? 16 : 36,
    overscan: 5,
  });

  const visibleColumns = table
    .getAllColumns()
    .filter((column) => column.getIsVisible());

  const canSearch = Object.values(columns).flat().length > 8;

  return (
    <div className="w-[480px]">
      <div className="flex items-center justify-between py-2 pl-2 pr-3">
        <DropdownCheckbox
          className="w-max p-0 pl-1 hover:bg-surface-background"
          checked={isAllFilteredVisible}
          onSelect={(event) => event.preventDefault()}
          onCheckedChange={toggleAllFiltered}
        >
          {visibleColumns.length} {pluralize(visibleColumns.length, "column")}{" "}
          selected
        </DropdownCheckbox>
        {table.options.meta?.name && (
          <RestoreDefault tableName={table.options.meta?.name} />
        )}
      </div>
      <DropdownSeparator />
      {canSearch && (
        <Input
          wrapperClassName="mx-3 mt-2"
          value={searchTerm.value ?? ""}
          onChange={(e) => {
            searchTerm.setDelayed(e.target.value);
          }}
          onKeyDown={(ev) => {
            if (ev.key !== "Escape") {
              ev.stopPropagation();
            }
          }}
          before={<SearchIcon className="text-secondary-foreground" />}
          after={
            <IconButton
              icon={<CloseIcon />}
              onClick={() => {
                searchTerm.setImmediate("");
              }}
            />
          }
          placeholder="Search..."
        />
      )}
      <div ref={parentRef} className="mt-2 max-h-[280px] overflow-y-auto">
        {flattenedItems.length > 0 ? (
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualItem) => {
              const item = flattenedItems[virtualItem.index];
              if (!item) return null;

              const style: React.CSSProperties = {
                position: "absolute",
                height: `${virtualItem.size}px`,
                transform: `translateY(${virtualItem.start}px)`,
                width: "100%",
              };

              if (item.type === "label") {
                return (
                  <div key={item.label} style={style}>
                    <DropdownLabel className="px-2">{item.label}</DropdownLabel>
                  </div>
                );
              }

              const { column, groupLabel } = item;
              const label = column.columnDef.meta?.label || column.id;

              return (
                <div key={column.id} style={style}>
                  <DropdownCheckbox
                    className={cn("w-full pr-3", groupLabel ? "pl-6" : "pl-3")}
                    checked={column.getIsVisible()}
                    onSelect={(event) => event.preventDefault()}
                    onCheckedChange={(value) =>
                      column.toggleVisibility(!!value)
                    }
                  >
                    <Truncate text={label}>
                      <Highlight
                        text={label}
                        highlight={searchTerm.debouncedValue}
                      />
                    </Truncate>
                  </DropdownCheckbox>
                </div>
              );
            })}
          </div>
        ) : (
          <span className="flex justify-center py-2 text-xs text-secondary-foreground">
            No columns found
          </span>
        )}
      </div>
    </div>
  );
}

function RestoreDefault({ tableName }: { tableName: string }) {
  return (
    <Button
      className="p-0 text-xs text-secondary-foreground"
      onClick={() => resetTableVisibility(tableName)}
    >
      Restore defaults
    </Button>
  );
}

function ExportButton({
  table,
  canExport,
}: {
  table: Table<any>;
  canExport: boolean;
}) {
  if (!canExport) {
    return <div></div>;
  }
  return (
    <Button
      className="p-0 text-xs text-secondary-foreground hover:text-primary-foreground"
      onClick={() =>
        exportCSV(table.getFilteredRowModel().rows, table.getAllFlatColumns())
      }
      icon={<DownloadIcon />}
    >
      Export
    </Button>
  );
}
